// src/routes/waypoint/index.tsx
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Mic, Plus } from "lucide-react";
import { CompetencyProfileCard } from "@/components/waypoint/CompetencyProfileCard";
import { useCompetencyProfile, useWaypointSessions } from "@/hooks/use-waypoint-sessions";
import { useSession } from "@/lib/auth-client";

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const DIFFICULTY_LABELS = {
  foundation: "Foundation",
  standard: "Standard",
  stretch: "Stretch",
} as const;

const STATUS_LABELS = {
  briefing: "Ready",
  active: "In progress",
  ended: "Ended",
} as const;

export function WaypointIndexPage() {
  const navigate = useNavigate();
  const { data: sessionAuth } = useSession();
  const { data, isLoading } = useWaypointSessions();
  const { data: profileData } = useCompetencyProfile();

  const firstName = sessionAuth?.user?.name?.split(" ")[0] ?? "there";
  const sessions = data?.data ?? [];
  const endedSessions = sessions.filter((s) => s.status === "ended");

  // Summary stats (from session summaries; debriefs need separate fetch so we approximate)
  const totalSessions = data?.total ?? 0;

  return (
    <div style={{ animation: "wp-fade 0.3s ease-out" }}>
      {/* Greeting */}
      <div style={{ marginBottom: 32 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: "#407EC1",
            marginBottom: 4,
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          Dashboard
        </p>
        <h1
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: 28,
            color: "#001847",
            margin: 0,
          }}
        >
          Good to see you, {firstName}
        </h1>
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
          marginBottom: 40,
        }}
      >
        {[
          { label: "Sessions completed", value: endedSessions.length, color: "#2EA86A" },
          { label: "Total sessions", value: totalSessions, color: "#407EC1" },
          {
            label: "Active session",
            value: sessions.find((s) => s.status === "active") ? "In progress" : "—",
            color: "#E8A93C",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e2e8f0",
              padding: "20px 24px",
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "#64748b",
                margin: "0 0 8px",
              }}
            >
              {stat.label}
            </p>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                fontSize: 28,
                color: stat.color,
                margin: 0,
                lineHeight: 1,
              }}
            >
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Recent sessions + right column */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 360px",
          gap: 24,
          alignItems: "start",
        }}
      >
        {/* Recent sessions */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <h2
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: "#001847",
                margin: 0,
              }}
            >
              Recent sessions
            </h2>
            {(data?.total ?? 0) > 5 && (
              <Link
                to="/dashboard/waypoint/history"
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#407EC1",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                View all
                <ArrowRight style={{ width: 12, height: 12 }} />
              </Link>
            )}
          </div>

          {isLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  style={{
                    height: 64,
                    borderRadius: 10,
                    background: "#f0f4fb",
                    animation: "pulse 2s infinite",
                  }}
                />
              ))}
            </div>
          )}

          {!isLoading && sessions.length === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 0",
                color: "#94a3b8",
                gap: 12,
              }}
            >
              <Mic style={{ width: 40, height: 40, opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: 14 }}>No sessions yet. Start one →</p>
            </div>
          )}

          {!isLoading && sessions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Header row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr 1fr 0.9fr auto",
                  gap: 12,
                  padding: "0 16px 8px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                {["Scenario", "When", "Status", ""].map((h) => (
                  <span
                    key={h}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "#94a3b8",
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {sessions.map((session) => {
                const isEnded = session.status === "ended";
                return (
                  <button
                    type="button"
                    key={session.id}
                    onClick={() =>
                      navigate({
                        to: isEnded
                          ? "/dashboard/waypoint/$sessionId/debrief"
                          : "/dashboard/waypoint/$sessionId",
                        params: { sessionId: session.id },
                      })
                    }
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.6fr 1fr 0.9fr auto",
                      gap: 12,
                      padding: "14px 16px",
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      alignItems: "center",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = "#407EC1";
                      (e.currentTarget as HTMLButtonElement).style.boxShadow =
                        "0 2px 8px rgba(30,108,199,0.10)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#001847",
                          margin: "0 0 2px",
                        }}
                      >
                        {session.scenarioName ?? "Session"}
                      </p>
                      <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
                        {DIFFICULTY_LABELS[session.difficulty]}
                      </p>
                    </div>
                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      {timeAgo(session.createdAt)}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "3px 8px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        background:
                          session.status === "ended"
                            ? "#E4F4EC"
                            : session.status === "active"
                              ? "#FEF3C7"
                              : "#E8F2FB",
                        color:
                          session.status === "ended"
                            ? "#2EA86A"
                            : session.status === "active"
                              ? "#E8A93C"
                              : "#407EC1",
                      }}
                    >
                      {STATUS_LABELS[session.status]}
                    </span>
                    <ArrowRight style={{ width: 14, height: 14, color: "#94a3b8" }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right column: start new + competency profile */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Start new session card */}
          <div
            style={{
              background: "linear-gradient(135deg, #001847 0%, #0E2658 100%)",
              borderRadius: 16,
              padding: 28,
              color: "#E6EEF8",
            }}
          >
            <p
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: "#7FC2EC",
                margin: "0 0 12px",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Practice
            </p>
            <h3
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                fontSize: 20,
                color: "#FAFBFD",
                margin: "0 0 10px",
              }}
            >
              Start a new session
            </h3>
            <p
              style={{
                fontSize: 13,
                color: "rgba(230,238,248,0.7)",
                lineHeight: 1.6,
                margin: "0 0 24px",
              }}
            >
              Pick a scenario, set the difficulty, and step into a high-stakes conversation.
            </p>
            <button
              type="button"
              onClick={() => navigate({ to: "/dashboard/waypoint/scenarios" })}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 20px",
                background: "#1E6CC7",
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
                color: "#fff",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                width: "100%",
                justifyContent: "center",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "#2D9BD6";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "#1E6CC7";
              }}
            >
              <Plus style={{ width: 16, height: 16 }} />
              Choose a scenario
            </button>
          </div>

          {/* Competency profile card — only shown once there's data */}
          {profileData && profileData.dimensions.length > 0 && (
            <CompetencyProfileCard
              dimensions={profileData.dimensions}
              sessionCount={profileData.sessionCount}
            />
          )}
        </div>
      </div>
    </div>
  );
}
